import Image from '@atlaskit/image';
import { Modal } from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import React, { Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl-next';
import { graphql, useLazyLoadQuery, useRelayEnvironment } from 'react-relay';

import { useFeatureGate } from '@townsquare/stat-sig/gate';
import { useWorkspaceStore } from '@townsquare/workspace-store';
import { useWorkspaceType } from '@townsquare/workspace-store/hooks';

import { saveOnboarding } from '../../mutations/SaveOnboardingMutation';
import { useOnboardingCover } from '../PopupOnboarding/onboardingCover';

import { BetterUpdates_onboardingKeyQuery } from './__generated__/BetterUpdates_onboardingKeyQuery.graphql';
import UpdateImage from './base-image.png';
import FirstEpic from './epic-1.png';
import SecondEpic from './epic-2.png';
import ThirdEpic from './epic-3.png';
import FirstItem from './first-item.png';
import HeaderDark from './header-dark.png';
import HeaderLight from './header-light.png';
import { Popin1, Popin2, Popin3, RotatingBox } from './styled';

/* eslint-disable @atlaskit/design-system/no-physical-properties */
const containerStyles = xcss({
  position: 'relative',
  borderTopLeftRadius: 'border.radius',
  borderTopRightRadius: 'border.radius',
  overflow: 'hidden',
});

const firstItemStyles = xcss({
  position: 'absolute',
  top: '95px',
  left: '192px',
});

const firstEpicStyles = xcss({
  position: 'absolute',
  top: '124px',
  left: '265px',
});

const asteriskStyles = xcss({
  position: 'absolute',
  top: '78px',
  left: '345px',
  animation: '2s linear 0s slidein',
});

const Asterisk = () => (
  <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.2385 12.8158C17.9279 12.7563 27.152 10.526 24.7324 10.0421C23.8349 10.2935 23.1374 10.0394 22.6886 10.7206C22.5589 9.9177 20.7989 11.1991 20.5286 11.2478C20.4069 10.6395 17.6494 12.2048 18.0522 11.6398C16.8005 11.656 15.2461 12.094 13.6294 12.6076C12.8373 12.0696 16.2328 8.23075 14.8054 9.0607C14.9054 8.84443 15.2893 7.90905 15.238 8.52813C15.6894 7.99826 20.6043 -0.112063 18.5605 1.26128C17.1817 2.93471 16.1463 5.7382 14.9784 7.12236C13.843 7.59005 13.0563 11.6074 11.7911 11.2073C11.053 10.3043 9.14981 -0.752764 7.79809 0.228585C8.03329 1.08828 7.81972 1.85874 8.47666 2.27237C7.67374 2.40213 8.95516 4.16207 9.00382 4.43242C8.40907 4.54596 9.95003 7.31429 9.39583 6.90878C9.40664 7.69007 9.58237 8.59303 9.83649 9.55275C9.49856 9.29592 9.12819 8.70926 8.91461 8.62275C6.63832 7.70087 4.91352 5.58138 2.5426 4.43242C0.550168 6.97095 18.1685 13.3754 7.24389 14.3324C5.18928 15.6733 2.45879 15.8788 0.166277 17.1683C-0.0310742 19.961 13.0374 12.2562 9.26606 16.6817C8.67941 18.5525 7.80891 20.6531 6.60587 22.4968C5.8381 29.5285 12.3669 10.4259 12.0966 17.7144C12.9968 19.4554 13.8673 21.5533 14.3134 23.7134C17.5061 28.3957 13.1482 16.0086 12.6913 14.254C14.4215 13.516 21.1098 21.8858 22.321 20.5666C22.3777 20.38 21.4153 19.3446 21.1234 19.2338C20.926 19.3338 20.826 19.4149 20.7097 19.231C21.2964 18.666 19.2391 17.9523 19.0444 17.7577C19.477 17.3278 16.614 15.9301 17.3088 15.9518C16.7735 15.2029 15.9544 14.4487 15.0217 13.689C16.6762 13.37 17.3953 12.2021 17.2439 12.8104L17.2385 12.8158Z"
      fill="#101214"
    />
  </svg>
);

const exProductAtlasLink = 'https://community.atlassian.com/t5/Atlas-Group-articles/Atlas-is-evolving/ba-p/2686154';
const newToAtlasLink =
  'https://support.atlassian.com/platform-experiences/docs/track-goals-and-projects-across-atlassian-products/';

// The following animation is disallowed/not-to-be-used currently. We will replace the current header
// with the animation once we have design approval.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const HeaderAnimation = () => {
  return (
    <Box xcss={containerStyles}>
      <img src={UpdateImage} width={600} />
      <Box xcss={firstEpicStyles}>
        <Popin3>
          <img src={ThirdEpic} width={199} />
        </Popin3>
      </Box>
      <Box xcss={firstEpicStyles}>
        <Popin2>
          <img src={SecondEpic} width={192} />
        </Popin2>
      </Box>
      <Box xcss={firstEpicStyles}>
        <Popin1>
          <img src={FirstEpic} width={194} />
        </Popin1>
      </Box>
      <Box xcss={firstItemStyles}>
        <img src={FirstItem} width={168} />
      </Box>
      <Box xcss={asteriskStyles}>
        <RotatingBox>
          <Asterisk />
        </RotatingBox>
      </Box>
    </Box>
  );
};

const Inner = () => {
  const intl = useIntl();
  const environment = useRelayEnvironment();

  const [{ cloudId }] = useWorkspaceStore();

  const data = useLazyLoadQuery<BetterUpdates_onboardingKeyQuery>(
    graphql`
      query BetterUpdates_onboardingKeyQuery($cloudId: String!) {
        currentUser {
          onboarding(first: 1, filter: { keys: [GLOBAL_HOME] }) {
            __id
            edges {
              node {
                key
              }
            }
          }
        }
        workspaceHadProductAtlasPreviously(cloudId: $cloudId)
      }
    `,
    {
      cloudId,
    },
  );

  const { setShown } = useOnboardingCover();

  const onboardingId = data?.currentUser?.onboarding?.__id ?? '';
  const hadProductAtlasPreviously = data.workspaceHadProductAtlasPreviously ?? false;
  const isSeen = data?.currentUser?.onboarding?.edges?.some(e => e?.node?.key === 'GLOBAL_HOME');

  const [isOpen, setOpen] = useState(!isSeen);

  useEffect(() => {
    if (isSeen) {
      setShown(false);
    } else if (isOpen) {
      setShown(true);
    }
  }, [isOpen, isSeen, setShown]);

  const onCompleted = () => {
    saveOnboarding(environment, { onboardingKey: 'GLOBAL_HOME' }, [onboardingId]);
    setOpen(false);
    setShown(false);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      // When we are ready to switch to the new animation, use the following line.
      // header={() => <HeaderAnimation />}
      // Can be removed and replaced with the above after design refresh.
      header={() => <Image src={HeaderLight} srcDark={HeaderDark} />}
      heading={
        hadProductAtlasPreviously
          ? intl.formatMessage({
              id: 'townsquare.goals-directory.changeboarding.title-ex-product',
              description: 'Title for changeboarding for customers who have used atlas before',
              defaultMessage: 'Atlas has evolved!',
            })
          : intl.formatMessage({
              id: 'townsquare.goals-directory.changeboarding.title',
              description: 'Title for changeboarding for new customers beginning to use the product',
              defaultMessage: 'Goals and projects are here!',
            })
      }
      actions={[
        {
          appearance: 'subtle',
          text: intl.formatMessage({
            id: 'townsquare.goals-directory.changeboarding.learn-more',
            description: 'Button to learn more about the new global experiences features',
            defaultMessage: 'Learn more',
          }),
          href: hadProductAtlasPreviously ? exProductAtlasLink : newToAtlasLink,
          rel: 'noopener noreferrer',
          target: '_blank',
        },
        {
          appearance: 'primary',
          text: intl.formatMessage({
            id: 'townsquare.goals-directory.changeboarding.complete-button',
            description: 'Button to close the changeboarding modal, saying "got it" as in the user has understood',
            defaultMessage: 'Got it',
          }),
          onClick: onCompleted,
        },
      ]}
    >
      {hadProductAtlasPreviously
        ? intl.formatMessage({
            id: 'townsquare.goals-directory.changeboarding.description-previous-atlas-product',
            description: 'Body content for changeboarding for customers with experience in Atlas as a product',
            defaultMessage:
              'As announced at Team 24, Atlas is evolving into Platform experiences for all Atlassian customers. Your data, including goals, projects, teams, tags, and kudos, has been fully migrated, with no changes to your experience.',
          })
        : intl.formatMessage({
            id: 'townsquare.goals-directory.changeboarding.description-new-to-projects-and-goals',
            description: 'Body content for the changeboarding forcustomers that are new to projects and goals',
            defaultMessage:
              'Use goals to track progress and easily report outcomes. Capture work with projects and communicate progress regularly to stay in sync, async. Watch this space as we continue to add new features!',
          })}
    </Modal>
  );
};

export const BetterUpdatesChangeboarding = () => {
  const workspaceType = useWorkspaceType();
  const isEnabled = useFeatureGate('enable_home_changeboarding') && workspaceType === 'GLOBAL_EXPERIENCE';

  if (!isEnabled) {
    return null;
  }

  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={null}>
        <Inner />
      </Suspense>
    </ErrorBoundary>
  );
};
